import { ExternalLink } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { ScoreBadge } from './ScoreBadge'
import { formatDistanceToNow } from 'date-fns'
import type { KnowledgeSearchResult } from '@/types/knowledge'

interface KnowledgeSearchResultCardProps {
  result: KnowledgeSearchResult
}

export function KnowledgeSearchResultCard({ result }: KnowledgeSearchResultCardProps) {
  const truncatedContent = result.content.length > 300
    ? result.content.slice(0, 300) + '...'
    : result.content

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary hover:underline truncate flex items-center gap-1"
            >
              {result.title}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
            <Badge variant="secondary">{result.sourceType}</Badge>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {formatDistanceToNow(new Date(result.lastModified), { addSuffix: true })}
          </span>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">
          {truncatedContent}
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          <ScoreBadge label="Fused" score={result.fusedScore} />
          {result.lexicalScore != null && (
            <ScoreBadge label="Lexical" score={result.lexicalScore} />
          )}
          {result.vectorScore != null && (
            <ScoreBadge label="Vector" score={result.vectorScore} />
          )}
          {result.rerankedScore != null && (
            <ScoreBadge label="Reranked" score={result.rerankedScore} />
          )}
          <span className="text-xs font-mono text-muted-foreground ml-auto">
            {result.documentId}
          </span>
          {result.language && (
            <Badge variant="outline">{result.language}</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
